import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useScope } from "@/lib/scope";

/** Scope registry (all / clusters / stores) + period catalogue + data anchor date. */
export const useScopes = () => useQuery({ queryKey: ["v2-scopes"], queryFn: api.getScopes, staleTime: 5 * 60_000 });

/** Live count feed status - polled every minute so the header pill and banners stay honest. */
export const useFeedStatus = () => useQuery({ queryKey: ["feed-status"], queryFn: api.getFeedStatus, refetchInterval: 60_000, staleTime: 30_000 });

/** v1 site registry (still used by the Live Count Feed brief + POS upload store list). */
export const useSites = () => useQuery({ queryKey: ["sites"], queryFn: api.getSites, staleTime: 5 * 60_000 });

export const useCountV2 = () => {
  const { params } = useScope();
  return useQuery({ queryKey: ["v2-count", params], queryFn: () => api.getCount(params), placeholderData: keepPreviousData });
};

export const useSalesV2 = () => {
  const { params } = useScope();
  return useQuery({ queryKey: ["v2-sales", params], queryFn: () => api.getSalesV2(params), placeholderData: keepPreviousData });
};

/** Executive summary is always group-wide: only the period travels. */
export const useSummaryV2 = () => {
  const { params } = useScope();
  const p = { period: params.period, start: params.start, end: params.end };
  return useQuery({ queryKey: ["v2-summary", p], queryFn: () => api.getSummary(p), placeholderData: keepPreviousData });
};

export const useCalendar = (params, enabled = true) => useQuery({ queryKey: ["calendar", params], queryFn: () => api.getCalendar(params), enabled, placeholderData: keepPreviousData });

/** Next-30-days forecast brief for the Executive Summary card (group-wide). */
export const useForecastBrief = (scope = "all", horizon = 30) => useQuery({ queryKey: ["v2-forecast-brief", scope, horizon], queryFn: () => api.getForecastBrief({ scope, horizon }), staleTime: 5 * 60_000 });

export const useUsers = (enabled = true) => useQuery({ queryKey: ["users"], queryFn: api.listUsers, enabled });

/** After anything that changes shared data (POS upload, calendar event) every analytics view must refetch. */
export const useInvalidateAnalytics = () => {
  const qc = useQueryClient();
  return () => {
    ["v2-count", "v2-sales", "v2-summary", "v2-forecast", "v2-forecast-brief", "calendar", "insights-v2", "sites"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
};
